import type { ProductCatalog, ProductId, Sku } from '~/product-catalog/api/model';
import type { productDictionary } from '../constants/map-dictionary';
import type { EnhancedProduct, ProductInformation } from './product-catalog-repository.types';

export type PlansByProduct = Record<keyof typeof productDictionary, ProductInformation[]>;
type AllowanceDictionary = Record<Sku, ProductInformation>;

export class ProductCatalogRepository {
  #productCatalog: EnhancedProduct[];

  #allowanceDictionary: AllowanceDictionary;

  constructor(productCatalog: ProductCatalog) {
    this.#allowanceDictionary = ProductCatalogRepository.#createAllowanceDictionary(productCatalog);
    this.#productCatalog = productCatalog;
  }

  getPlansByProductId(productId: ProductId): EnhancedProduct | undefined {
    return this.#productCatalog.find((product) => product.id === productId);
  }

  getProductBySku(sku: Sku): ProductInformation | undefined {
    return this.#allowanceDictionary[sku];
  }

  getBasicAllowanceSkus(): Sku[] {
    return Array.from(Object.values(this.#allowanceDictionary))
      .filter(({ allowance }) => !allowance.chargeable && allowance.selfServeEnabled)
      .map(({ allowance }) => allowance.sku);
  }

  static #createAllowanceDictionary(productCatalog: ProductCatalog): AllowanceDictionary {
    const dictionary: AllowanceDictionary = {};
    productCatalog.forEach((product) =>
      product.plans.forEach((plan) => {
        plan.allowances.forEach((allowance) => {
          dictionary[allowance.sku] = {
            product: {
              id: product.id,
              name: product.name,
            },
            plan: {
              id: plan.id,
              name: plan.name,
              planTier: plan.planTier,
            },
            allowance,
          };
        });
      }),
    );
    return dictionary;
  }
}
